import { ApplicationConfig, InjectionToken, LOCALE_ID, TransferState, makeStateKey } from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterConfig, withViewTransitions } from '@angular/router';
import { appRoutes } from './app.routes';
import { provideClientHydration, withI18nSupport } from '@angular/platform-browser';
import { provideServiceWorker } from '@angular/service-worker';
import { provideHttpClient, withFetch } from '@angular/common/http';
import { environment } from '../environments/environment';
import { DEFAULT_CONTENT_METADATA_CONFIG, providePageMetadata, withSchemaOrg, withTwitterCard, withOpenGraph } from '@upupa/common';
import { provideAnimations } from '@angular/platform-browser/animations';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha';
import { IMAGE_LOADER, ImageLoaderConfig } from '@angular/common';
import { DataService } from './data-service';

export const APP_SEO = new InjectionToken('APP_SEO');
export function isBrowser(): boolean {
    return typeof document !== 'undefined' && typeof window !== 'undefined';
}

function readFromState(key: string) {
    if (!isBrowser()) return;
    const state = JSON.parse(document.getElementById('ng-state').textContent);
    return state[key];
}

export async function appConfig(): Promise<ApplicationConfig> {
    const config =
        readFromState('APP_SEO') ??
        (await fetch(`${environment.api_base}/page?path=seo-fallback`)
            .then((r) => r.json())
            .catch((e) => console.error(e))
            .then((r) => r?.data?.[0]));

    return {
        providers: [
            {
                provide: APP_SEO,
                useFactory: (state) => {
                    state.set(makeStateKey<any>('APP_SEO'), config);
                    return config;
                },
                deps: [TransferState],
            },
            provideHttpClient(withFetch()),
            provideClientHydration(withI18nSupport()),

            provideAnimations(),
            // provideZoneChangeDetection({ eventCoalescing: true }),
            provideRouter(
                appRoutes,
                withComponentInputBinding(),
                withRouterConfig({ resolveNavigationPromiseOnError: true }),
                withInMemoryScrolling({
                    anchorScrolling: 'enabled',
                    scrollPositionRestoration: 'enabled',
                }),
                // withDebugTracing(),
                withViewTransitions()
            ),
            provideServiceWorker('ngsw-worker.js', {
                enabled: environment.production,
                registrationStrategy: 'registerWhenStable:30000',
            }),

            { provide: RECAPTCHA_V3_SITE_KEY, useValue: '6LdccS4qAAAAAGi5HCY2woDZ2WX3NtjxZ4-UcNID' },
            { provide: DataService, useClass: DataService },
            providePageMetadata(
                {
                    useFactory: (appSeo: any, locale: string) => {
                        const lang = (locale ?? 'en').split('-')[0];

                        const meta = appSeo ?? {};
                        const suffix = meta.translations?.[lang]?.seo?.title ?? meta.seo?.title ?? '';
                        return {
                            ...DEFAULT_CONTENT_METADATA_CONFIG,
                            titleTemplate: (title: string) => (title?.length && suffix?.length ? `${title} | ${suffix}` : title || suffix),
                            canonicalBaseUrl: environment.base_url,
                            fallback: { ...meta.seo, ...meta.translations?.[lang]?.seo },
                            imageLoading: imageLoading(),
                        };
                    },
                    deps: [APP_SEO, LOCALE_ID],
                },
                withTwitterCard({
                    imageLoading: imageLoading(1200, 628),
                }),
                withOpenGraph({
                    imageLoading: imageLoading(1200, 630),
                }),
                withSchemaOrg({
                    imageLoading: imageLoading(),
                })
            ),

            {
                provide: IMAGE_LOADER,
                useValue: (config: ImageLoaderConfig) => {
                    if (!config.src) return '';
                    if (config.src.startsWith('/storage/') || config.src.startsWith('storage/')) return getResizedImageLink(config.src, config.width);
                    return config.src;
                },
            },
        ],
    };
}

export function imageLoading(w?: number, h?: number) {
    return (config: { src?: string; size?: string }) => {
        return getResizedImageLink(config.src + '', w, h);
    };
}
export function getResizedImageLink(path: string, w?: number, h?: number): string {
    // const base = environment.base_url;

    const removeLeadingSlash = (path: string) => {
        return path.startsWith('/') ? path.slice(1) : path;
    };

    const [base, qps] = removeLeadingSlash(path).split('?');
    const queryParams = (qps ?? '').split('&').map((q) => q.split('='));

    const imageResizeOptions = new Map<string, string>([
        ['view', '1'],
        ['attachment', 'inline'],
    ]);

    if (w) imageResizeOptions.set('w', w + '');
    if (h) imageResizeOptions.set('h', h + '');

    for (const q of queryParams) {
        if (q[0] === 'attachment') continue;
        imageResizeOptions.set(q[0], q[1]);
    }
    const qStr =
        '?' +
        Array.from(imageResizeOptions.entries())
            .map(([k, v]) => `${k}=${v}`)
            .join('&');

    return environment.base_url + '/' + base + qStr;
}
